import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AppError } from '../../api/errors.js';
import type { DatasetOwnershipFilter } from '../../constants/index.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type {
  TestSet,
  TestSetSearchResult,
} from '../../services/testSetService.interface.js';
import { TestSetService } from '../../services/testSetService.js';

import type {
  UseTestSetsParams,
  UseTestSetsResult,
} from './useTestSets.interface.js';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

function dedup(existing: TestSet[], incoming: TestSet[]): TestSet[] {
  const seen = new Set(existing.map((d) => d.id));
  return incoming.filter((d) => !seen.has(d.id));
}

function toOwnerObjectId(
  filter?: DatasetOwnershipFilter,
  userObjectId?: string | null,
): string | undefined {
  if (filter === 'My Datasets' && userObjectId) return userObjectId;
  return undefined;
}

export function useTestSets(params: UseTestSetsParams = {}): UseTestSetsResult {
  const client = useDataHubClient();
  const service = useMemo(() => new TestSetService(client), [client]);

  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [continuationToken, setContinuationToken] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | undefined>(
    undefined,
  );
  const [refreshCount, setRefreshCount] = useState(0);

  const prefetchedRef = useRef<TestSetSearchResult | null>(null);
  const prefetchTokenRef = useRef<string | null>(null);

  const refresh = useCallback(() => {
    setRefreshCount((n) => n + 1);
  }, []);

  const [debouncedQuery, setDebouncedQuery] = useState(params.query ?? '');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(params.query ?? '');
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [params.query]);

  const ownerUserObjectId = toOwnerObjectId(
    params.ownershipFilter,
    params.userObjectId,
  );

  useEffect(() => {
    let cancelled = false;

    prefetchedRef.current = null;
    prefetchTokenRef.current = null;

    if (params.enabled === false) {
      setTestSets([]);
      setTotalCount(null);
      setContinuationToken(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setErrorStatusCode(undefined);
    setTestSets([]);
    setTotalCount(null);
    setContinuationToken(null);

    service
      .searchTestSets({
        query: debouncedQuery || undefined,
        ownerUserObjectId,
        pageSize: PAGE_SIZE,
        includeCount: true,
      })
      .then((result) => {
        if (cancelled) return;
        setTestSets(result.items);
        setTotalCount(result.totalCount);
        setContinuationToken(result.continuationToken);
        setIsLoading(false);

        if (result.continuationToken) {
          prefetchTokenRef.current = result.continuationToken;
          service
            .searchTestSets({
              query: debouncedQuery || undefined,
              ownerUserObjectId,
              pageSize: PAGE_SIZE,
              continuationToken: result.continuationToken,
              includeCount: false,
            })
            .then((prefetched) => {
              if (
                cancelled ||
                prefetchTokenRef.current !== result.continuationToken
              ) {
                return;
              }
              prefetchedRef.current = prefetched;
            })
            .catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load test sets',
        );
        setErrorStatusCode(
          err instanceof AppError ? err.statusCode : undefined,
        );
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    service,
    debouncedQuery,
    ownerUserObjectId,
    params.enabled,
    refreshCount,
  ]);

  const loadMore = useCallback(() => {
    if (!continuationToken || isLoadingMore) return;

    if (
      prefetchedRef.current &&
      prefetchTokenRef.current === continuationToken
    ) {
      const prefetched = prefetchedRef.current;
      prefetchedRef.current = null;
      prefetchTokenRef.current = null;

      setTestSets((prev) => [...prev, ...dedup(prev, prefetched.items)]);
      setContinuationToken(prefetched.continuationToken);

      if (prefetched.continuationToken) {
        prefetchTokenRef.current = prefetched.continuationToken;
        service
          .searchTestSets({
            query: debouncedQuery || undefined,
            ownerUserObjectId,
            pageSize: PAGE_SIZE,
            continuationToken: prefetched.continuationToken,
            includeCount: false,
          })
          .then((next) => {
            if (prefetchTokenRef.current !== prefetched.continuationToken) {
              return;
            }
            prefetchedRef.current = next;
          })
          .catch(() => undefined);
      }
      return;
    }

    setIsLoadingMore(true);
    service
      .searchTestSets({
        query: debouncedQuery || undefined,
        ownerUserObjectId,
        pageSize: PAGE_SIZE,
        continuationToken,
        includeCount: false,
      })
      .then((result) => {
        setTestSets((prev) => [...prev, ...dedup(prev, result.items)]);
        setContinuationToken(result.continuationToken);
        setIsLoadingMore(false);

        if (result.continuationToken) {
          prefetchTokenRef.current = result.continuationToken;
          service
            .searchTestSets({
              query: debouncedQuery || undefined,
              ownerUserObjectId,
              pageSize: PAGE_SIZE,
              continuationToken: result.continuationToken,
              includeCount: false,
            })
            .then((next) => {
              if (prefetchTokenRef.current !== result.continuationToken) return;
              prefetchedRef.current = next;
            })
            .catch(() => undefined);
        }
      })
      .catch((err: unknown) => {
        setError(
          err instanceof Error ? err.message : 'Failed to load more test sets',
        );
        setErrorStatusCode(
          err instanceof AppError ? err.statusCode : undefined,
        );
        setIsLoadingMore(false);
      });
  }, [
    continuationToken,
    isLoadingMore,
    service,
    debouncedQuery,
    ownerUserObjectId,
  ]);

  return {
    testSets,
    totalCount,
    isLoading,
    error,
    errorStatusCode,
    isLoadingMore,
    hasMore: continuationToken !== null,
    loadMore,
    refresh,
  };
}
